import {
  ActionHeader,
  Strong,
  StyledDarkSectionTitle,
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
} from "@/common/styles";
import { useEffect, useState, Suspense, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { BookingQueueContainer } from "./styles";
import { labels } from "@/util/label";
import API_ENDPOINTS from "@/util/api";
import api from "@/services/axios_custom";
import {
  hasCheckSecurity,
  isNonEmptyVariable,
  toQueryString,
} from "@/util/common_function";
import dayjs from "dayjs";
import DataTable from "@/common/elements/DataTable";
import BookingsPage from "../Bookings";
import Modal from "@/common/elements/Modal";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import Button from "@/common/elements/Button";
import { setBookingsNull } from "@/slices/bookingsSlice";
import { setBookingQueueSearch } from "@/slices/bookingQueueSearchSlice";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import { MODULES_LIST_WITH_ID } from "@/util/common_variable";
import NoAccess from "@/components/BookingsDetails/NoAccess";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";
import { Popconfirm } from "antd";

// Mirrors SearchBookingQueue's (the left "Search Criteria" sidebar) own
// default formData for the /release-queue route - searchType and
// custodyStatus are always present as baseline entries even with nothing
// user-entered, so "is a filter applied" means more than these 2.
const DEFAULT_BOOKING_QUEUE_FILTER = [
  { filterKey: "searchType", value: "Starts With" },
  { filterKey: "custodyStatus", value: "In Custody" },
];

const ReleaseQueue = () => {
  const { bookingQueueFilter } = useSelector((state) => state?.bookingQueue);
  const { headerHeight } = useOutletContext();
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const dispatch = useDispatch();
  const [showStepModal, setShowStepModal] = useState(false);
  const [loadingAPI, setLoadingAPI] = useState(false);
  const [nextBookingStep, setNextBookingStep] = useState(null);
  // Drives the step modal's real footer Save/Cancel buttons - the actual
  // handlers live inside BookingsPage, reached via this ref.
  const bookingsPageRef = useRef(null);
  const [bqFooterState, setBqFooterState] = useState({
    isLoading: false,
    isModifyDisabled: false,
  });
  const [releaseQueueData, setReleaseQueueData] = useState({
    total: 0,
    headers: [
      { key: "booking", text: labels.booking },
      { key: "name", text: labels.name },
      { key: "currentloc", text: labels.currentLocation },
      { key: "scheduledDate", text: "Scheduled Release  Date" },
      { key: "charge", text: "Charge" },
      { key: "releaseReason", text: "Release Reason" },
      { key: "releaseTO", text: "Release To" },
    ],
    rows: [],
  });

  const sortKeyMap = {
    booking: "bookingNo",
    currentloc: "currentLocation",
    releaseTO: "releaseTo",
    name: "lName",
  };

  const [selectRQ, setSelectRQ] = useState(null);

  const prevFilterRef = useRef(bookingQueueFilter);
  const prevPaginationRef = useRef(pagination);
  const [isFilterChanged, setisFilterChanged] = useState(false);

  const [sortInfo, setSortInfo] = useState({ orderby: null, direction: "DESC" });
  const isPageRef = useRef(false);

  // Config button (reorder/resize columns) for the Release Queue worklist
  // grid — a deliberate deviation from legacy, same category as
  // Warrants/Visitor Log/Users/Staff's main lists: legacy's equivalent
  // grid (`ListID = "Bookings__ReadyForRelease"`,
  // `BookingsQueueInterface.asp:226-286`) is built with the low-level
  // CreateListWithButtons primitive and never wires SetupConfigButton
  // (confirmed in docs/plans/grid-column-config-bookings-rollout.md §4).
  // The user explicitly asked for it anyway. gridId uses this screen's own
  // "Bookings Queue" module context rather than "Bookings:..." — unlike
  // grids also reachable by drilling into a booking record (§4's
  // multi-context note), this worklist grid is queue-native only, so
  // there's no dual-context ambiguity to simplify away.
  const {
    headers: releaseQueueHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig(
    "Bookings Queue:ReadyForRelease",
    releaseQueueData.headers,
  );

  useEffect(() => {
    getReleaseQueue();
  }, []);

  useEffect(() => {
    const isFilterChangedLocal =
      JSON.stringify(prevFilterRef?.current) !==
      JSON.stringify(bookingQueueFilter);

    setisFilterChanged(() => isFilterChangedLocal);
    prevFilterRef.current = bookingQueueFilter;

    const isPaginationChanged =
      JSON.stringify(prevPaginationRef?.current) !== JSON.stringify(pagination);
    prevPaginationRef.current = pagination;

    if (isFilterChangedLocal && bookingQueueFilter?.length) {
      setPagination((prev) => ({
        pageIndex: 0,
        pageSize: 10,
      }));

      getReleaseQueue();
    }

    if (isPaginationChanged) {
      isPageRef.current = true;
      getReleaseQueue();
    }
  }, [bookingQueueFilter, pagination]);

  // useEffect(() => {
  //   if (!loadingAPI && bookingQueueFilter?.length) {
  //     getReleaseQueue();
  //   }
  // }, [pagination]);

  const getReleaseQueue = (sort = sortInfo) => {
    setLoadingAPI(true);
    const sf = JSON.parse(JSON.stringify(bookingQueueFilter));
    const data = sf?.map((sf) => {
      if (
        sf?.filterKey == "incarcerationDate" &&
        isNonEmptyVariable(sf?.value)
      ) {
        sf.value = dayjs(sf?.value)?.format("MM/DD/YYYY");
      }

      if (sf?.filterKey == "dob" && isNonEmptyVariable(sf?.value)) {
        sf.value = dayjs(sf?.value)?.format("MM/DD/YYYY");
      }

      return sf;
    });

    const text = JSON.stringify(data);
    let url = API_ENDPOINTS.releaseQueueList;
    const obj = {
      page: isFilterChanged ? 0 : pagination?.pageIndex,
      size: isFilterChanged ? 10 : pagination?.pageSize,
      direction: sort.direction,
      ...(sort.orderby && { orderby: sort.orderby }), // only add if set
    };
    const urlParam = toQueryString(obj);
    if (isNonEmptyVariable(urlParam)) {
      url += "?" + urlParam;
    }
    api
      .post(url, text)
      .then((response) => {
        const { total, rows, size } = response?.data;

        const capitalize = (str) =>
          str && str.charAt(0).toUpperCase() + str.slice(1);

        const bookData = {
          total: total,
          size: size,
          rows: rows.map((book) => {
            const fName = capitalize(book?.bio?.[0]?.fName || "");
            const lName = capitalize(book?.bio?.[0]?.lName || "");
            const mName = capitalize(book?.bio?.[0]?.mName || "");
            const suffix = book?.bio?.[0]?.suffix || "";
            const name = `${lName ? lName + ',' : ''} ${fName}${mName ? ' ' + mName : ''}${suffix ? ', ' + suffix : ''}`.trim();
            return {
              booking: book?.bookingNo || "",
              firstName: book?.bio?.[0]?.fName,
              lastName: book?.bio?.[0]?.lName,
              name: name,
              currentloc: book?.currentLocation || "",
              scheduledDate: book?.scheduledDate || "",
              releaseReason: book?.releaseReason || "",
              releaseTO: book?.releaseTO || "",
            };
          }),
        };

        setReleaseQueueData((prev) => ({ ...prev, ...bookData }));
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
        } else {
          console.error("An error occurred:", error);
        }
      })
      .finally(() => {
        setLoadingAPI(false);
      });
  };

  const onSelectRQ = (val) => {
    setSelectRQ(val);
  };

  const handleReleaseStep = () => {
    const obj = {
      key: "personal-information",
      value: selectRQ,
    };
    dispatch(setBookingsNull());
    setNextBookingStep(obj);
    setShowStepModal(true);
  };

  const handleReleaseStepModal = () => {
    setShowStepModal(() => false);
    setSelectRQ(() => null);
    dataTableRef?.current?.resetAll();
  };

  const bqCompleteSaveAction = () => {
    setShowStepModal(() => false);
    setSelectRQ(() => null);
    dataTableRef?.current?.resetAll();
    getReleaseQueue();
  };

  // Clears the applied filter (the left "Search Criteria" sidebar is a
  // separate component driven only by redux, so this can't reach into its
  // own form fields to reset them visually - it clears the actual filter/
  // result set the same way its own Reset button does).
  const onClearFilter = () => {
    dispatch(setBookingQueueSearch(DEFAULT_BOOKING_QUEUE_FILTER));
    setPagination({ pageIndex: 0, pageSize: 10 });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const columnKey = sorter?.order ? sorter.columnKey : null;
    const newSort = {
      orderby: columnKey ? (sortKeyMap[columnKey] ?? columnKey) : null,
      direction: sorter?.order === "ascend" ? "ASC"
                : sorter?.order === "descend" ? "DESC"
                : "DESC",
    };
    setSortInfo(newSort);
    if (isPageRef.current) {
      isPageRef.current = false;
      return;
    }
    getReleaseQueue(newSort);
  };

  return (
    <>
      <StyledGridRow justify="space-between">
        <StyledGridCol span={24}>
          <StyledDarkSectionTitle bgColor="rgb(4 28 45)">
            <span>Release Queue</span>
          </StyledDarkSectionTitle>
        </StyledGridCol>
      </StyledGridRow>
      {hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.module,
        LABELS_CAPITAL.VIEW,
        MODULES_LIST_WITH_ID.MODULE_RELEASESQUEUE
      ) ? (
        <BookingQueueContainer>
          <ActionHeader
            headerHeight={headerHeight}
            style={{ marginBottom: "10px" }}
            className="queue-action-header"
          >
            <div className="left">
              <StyledFlex align="center" gap="10px">
                <Button
                  color="primary"
                  type="primary"
                  variant="solid"
                  onClick={handleReleaseStep}
                  disabled={
                    selectRQ === null ||
                    loadingAPI ||
                    isConfiguring ||
                    !hasCheckSecurity(
                      Security_Types.functionality,
                      Entity_types.module,
                      LABELS_CAPITAL.ADD,
                      MODULES_LIST_WITH_ID.MODULE_RELEASESQUEUE
                    )
                  }
                >
                  Booking Details
                </Button>
              </StyledFlex>
              <StyledFlex align="center" gap="10px">
                <Button
                  color="primary"
                  type="primary"
                  variant="solid"
                  onClick={handleReleaseStepModal}
                  disabled={selectRQ === null || loadingAPI || isConfiguring}
                >
                  cancel
                </Button>
              </StyledFlex>
            </div>
            <div className="right">
              <StyledFlex align="center" gap="10px">
                {/* Config — reorder/resize columns; see gridId comment
                    above for why this deviates from legacy. */}
                {!isConfiguring ? (
                  <Button
                    color="primary"
                    type="primary"
                    variant="solid"
                    htmlType="button"
                    onClick={startConfiguring}
                  >
                    {configButtonLabel}
                  </Button>
                ) : (
                  <Popconfirm
                    title="Do you want to save the current list view configuration?"
                    onConfirm={confirmSave}
                    onCancel={discardChanges}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      color="primary"
                      type="primary"
                      variant="solid"
                      htmlType="button"
                    >
                      {configButtonLabel}
                    </Button>
                  </Popconfirm>
                )}

                {/* Only shown once a real filter is applied via the
                    Search Criteria sidebar - matches the rest of the app. */}
                {bookingQueueFilter?.length > 2 && (
                  <Button
                    color="primary"
                    type="primary"
                    variant="solid"
                    onClick={onClearFilter}
                    disabled={loadingAPI || isConfiguring}
                  >
                    Clear Filter
                  </Button>
                )}
                <Strong>{releaseQueueData?.total} Results</Strong>
              </StyledFlex>
            </div>
          </ActionHeader>

          <DataTable
            ref={dataTableRef}
            draggable={isConfiguring}
            width="100%"
            totalRecords={releaseQueueData?.total}
            headers={releaseQueueHeaders}
            data={releaseQueueData?.rows}
            allowTableSorting={true}
            serverSideSorting={true}
            onTableChange={handleTableChange}
            pagination={pagination}
            setPagination={setPagination}
            showCheckbox={false}
            rowUniqueId={"booking"}
            onRowClick={onSelectRQ}
            isAPiCallLoading={loadingAPI}
            noDataMessage="No Release queue found"
          />
        </BookingQueueContainer>
      ) : (
        <NoAccess />
      )}

      {/* Modal */}
      {showStepModal && (
        <Modal
          designPattern="ant"
          header={"Bookings"}
          show={showStepModal}
          onHide={handleReleaseStepModal}
          size="xxl"
          backdrop="static"
          width={"95%"}
          footerActions={[
            <Button
              key="rq-step-cancel"
              color="secondary"
              type="secondary"
              disabled={bqFooterState.isLoading}
              onClick={() => {
                bookingsPageRef.current?.triggerCancel();
                handleReleaseStepModal();
              }}
            >
              Cancel
            </Button>,
            <Button
              key="rq-step-save"
              color="warning"
              variant="solid"
              loading={bqFooterState.isLoading}
              disabled={bqFooterState.isLoading || bqFooterState.isModifyDisabled}
              onClick={() => bookingsPageRef.current?.triggerSave()}
            >
              Save Changes
            </Button>,
          ]}
        >
          <Suspense fallback={<p>Loading...</p>} style={{ minHeight: "300px" }}>
            <BookingsPage
              ref={bookingsPageRef}
              isBQ={true}
              nextBookingStep={nextBookingStep}
              bqCompleteSaveAction={bqCompleteSaveAction}
              onFooterStateChange={setBqFooterState}
            />
          </Suspense>
        </Modal>
      )}
    </>
  );
};

export default ReleaseQueue;
